import { createBrowserRouter, redirect } from 'react-router-dom';
import { ComponentType } from 'react';

import AppRoot from './root';
import ProtectedRoute from '../features/auth/components/protected-route';
import {
  ensureBalance,
  prefetchChainDetails,
  prefetchChainSummary,
  prefetchMarketHours,
  prefetchOptionContract,
  prefetchPositions,
  prefetchUnderlyingLastTrade,
  prefetchUnderlyingsMap,
  prefetchUserInfo,
  queryClient,
} from '@oai-frontend-monorepo/api';
import {
  useUnderlyingMapStore,
  useUserStore,
} from '@oai-frontend-monorepo/state-management';
import { parseToken } from '@oai-frontend-monorepo/utils';
import { oktaAuth } from '../libs/okta';
import AppError from '../components/feedback/errors/app-error.component';
import useUIStore from '../stores/ui';
import SignIn from './sign-in/sign-in.page';
import { ErrorBoundary } from '../components/errors/ErrorBoundary';
import SignUpPage from './sign-up/sign-up.page';
import { _renewToken } from '../libs/okta/useRenewOktaToken';
import logEvent from '../hooks/ga4/logEvent';

export const createRouter = () =>
  createBrowserRouter([
    {
      ErrorBoundary: AppError,
      hasErrorBoundary: true,
      path: '/',
      element: <SignIn />,
    },
    {
      ErrorBoundary: AppError,
      hasErrorBoundary: true,
      path: '/sign-in',
      element: <SignIn />,
    },
    {
      ErrorBoundary: AppError,
      hasErrorBoundary: true,
      path: '/sign-up',
      element: <SignUpPage />,
    },
    {
      path: '/set-password',
      lazy: async (): Promise<{ Component: ComponentType }> => {
        const SetPassword = await import('./set-password/set-password.page');
        return { Component: SetPassword.default };
      },
    },
    {
      path: '/forgot-password',
      lazy: async (): Promise<{ Component: ComponentType }> => {
        const ForgotPassword = await import(
          './forgot-password/forgot-password.page'
        );
        return { Component: ForgotPassword.default };
      },
    },
    {
      path: '/auth/callback',
      lazy: async (): Promise<{ Component: ComponentType }> => {
        const AuthCallback = await import('./auth-callback/auth-callback.page');
        return { Component: AuthCallback.default };
      },
    },
    {
      path: '/tradier/auth-callback',
      lazy: async (): Promise<{ Component: ComponentType }> => {
        const AuthTradier = await import('./auth-tradier/auth-tradier.page');
        return { Component: AuthTradier.default };
      },
    },

    {
      path: '/',
      element: (
        <ErrorBoundary fallback={AppError}>
          <ProtectedRoute>
            <AppRoot />
          </ProtectedRoute>
        </ErrorBoundary>
      ),

      loader: async ({ params, request, context }) => {
        console.log('hi');
        const token = useUserStore.getState().token;

        const parsed = parseToken(token);

        if (parsed && parsed.exp < Date.now() / 1000) {
          queryClient.clear();
          useUserStore.getState().setToken(undefined);
          oktaAuth.clearStorage();
          await oktaAuth.closeSession();
        }

        let balanceError = false;

        // const didSubscribe = request.url.includes('subscribed');

        // if (didSubscribe) {
        //   logEvent({
        //     category: 'click',
        //     label: 'registration',
        //     action: 'submit-credit-card-button',
        //   });

        //   const ensureSubscriberInGroup = async (retryAttempt = 1) => {
        //     console.log('here 1');

        //     if (retryAttempt > 8) {
        //       return false;
        //     }

        //     console.log('here 2');

        //     await new Promise((resolve) =>
        //       setTimeout(resolve, 500 * retryAttempt)
        //     );

        //     console.log('here 3');

        //     const token = await _renewToken();

        //     console.log(
        //       `Attempt to ensure subscriber in group ${retryAttempt}`,
        //       token?.groups
        //     );

        //     console.log('token', token);

        //     if (retryAttempt > 2) {
        //       // eslint-disable-next-line no-debugger
        //       debugger;
        //     }

        //     if (
        //       !token?.groups?.includes('Subscribers') &&
        //       !token?.groups?.includes('Traders')
        //     ) {
        //       return ensureSubscriberInGroup(retryAttempt + 1);
        //     }

        //     return true;
        //   };

        //   const subscriberGroup = await ensureSubscriberInGroup();

        //   if (!subscriberGroup) {
        //     throw new Error('An error occured, please try again');
        //   }

        //   return redirect('/dashboard');
        // }

        try {
          if (
            !request.url.includes('join-tradier') &&
            !request.url.includes('tradier/auth-callback') &&
            !request.url.includes('subscribers')
          ) {
            await ensureBalance();
          }

          prefetchUserInfo();
          prefetchMarketHours();
          prefetchUnderlyingsMap({
            onSuccess: useUnderlyingMapStore.getState().setMap,
          });
        } catch (error) {
          balanceError = true;
        }

        return null;
      },
      shouldRevalidate: ({ currentUrl, nextUrl }) => {
        return false;
      },
      children: [
        {
          path: '/subscribers',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Subscribers = await import('./subscribers/subscribers.page');
            return { Component: Subscribers.default };
          },
        },
        {
          path: '/blog',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const BlogPost = await import('./blog/blog-list.page');
            return { Component: BlogPost.default };
          },
        },
        {
          path: '/blog/:id',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const BlogPost = await import('./blog/id/blog-post.page');
            return { Component: BlogPost.default };
          },
        },
        {
          path: '/watchlist',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Watchlist = await import('./watchlist/edit-watchlist.page');
            return { Component: Watchlist.default };
          },
        },
        {
          path: '/account',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Account = await import('./account/account.page');
            return { Component: Account.default };
          },
        },
        {
          path: '/join-tradier',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const JoinTradier = await import(
              './join-tradier/join-tradier.page'
            );
            return { Component: JoinTradier.default };
          },
        },
        {
          path: '/dashboard',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Dashboard = await import('./dashboard/dashboard.page');
            return { Component: Dashboard.default };
          },
        },
        {
          path: '/welcome',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Welcome = await import('./welcome/welcome.page');
            return { Component: Welcome.default };
          },
        },
        {
          path: '/positions/:id',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            useUIStore.getState().setSidePanelOpen(false);

            const Position = await import('./positions/id/position.page');

            return { Component: Position.default };
          },
          loader: async () => {
            prefetchPositions();

            return null;
          },
        },
        {
          path: '/orders',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Orders = await import('./orders/orders.page');
            return { Component: Orders.default };
          },
        },
        {
          path: '/orders/:id',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Order = await import('./orders/id/order-id.page');
            return { Component: Order.default };
          },
        },
        {
          path: '/positions',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Positions = await import('./positions/positions-list.page');
            return { Component: Positions.default };
          },
        },
        {
          path: '/trade',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Search = await import('./trade/search.page');
            return { Component: Search.default };
          },
        },
        {
          path: '/trade/categories/:categoryId',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Categories = await import(
              './trade/categories/categories-id.page'
            );
            return { Component: Categories.default };
          },
        },
        {
          path: '/trade/:symbol',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Trade = await import('./trade/symbol/company.page');

            return { Component: Trade.default };
          },
          loader: async (req) => {
            useUIStore.getState().setSidePanelOpen(false);

            const symbol = req.params.symbol as string;

            // const expiration = new URL(req.request.url).searchParams.get(
            //   'expirationDate'
            // );

            // if (expiration) {
            //   const date = DateTime.fromFormat(expiration, 'yyyy-MM-dd', {
            //   })
            //     .startOf('day')
            //     .plus({ hour: 16 });

            //   if (date.isValid) {
            //     const diffInDays = date.diffNow('days').as('days');

            //     let range = 1;

            //     if (diffInDays > 0.5) {
            //       range = 5;
            //     }

            //     if (diffInDays > 5) {
            //       range = 30;
            //     }

            //     if (diffInDays > 30) {
            //       range = 90;
            //     }

            //     if (diffInDays > 90) {
            //       range = 365;
            //     }

            //     if (diffInDays > 365) {
            //       range = 365 * 5;
            //     }

            //     useChartStore.getState().setRange(range);
            //   }
            // }

            prefetchOptionContract({ symbol });
            prefetchUnderlyingLastTrade(symbol);
            prefetchChainSummary(symbol);
            prefetchChainDetails({ symbol, range: 365 * 5 });

            return null;
          },
          shouldRevalidate: ({ currentUrl, nextUrl }) => {
            // Only revalidate if the path changes
            return currentUrl.pathname !== nextUrl.pathname;
          },
        },
        {
          path: '/price-alerts',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const PriceAlerts = await import(
              './price-alerts/price-alerts.page'
            );
            return { Component: PriceAlerts.default };
          },
        },
        {
          path: '/notifications',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const Notifications = await import(
              './notifications/notifications.page'
            );
            return { Component: Notifications.default };
          },
        },
        {
          path: '*',
          lazy: async (): Promise<{ Component: ComponentType }> => {
            const NotFound = await import('./not-found/not-found.page');
            return { Component: NotFound.default };
          },
        },
      ],
      // add 404 page
    },
  ]);
